import { memo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import Breadcrumb from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";

import { useConnectionStore } from "stores/ConnectionStore";

import { FOTS } from "utility/typescript";

import ServiceConnectionInstructions from "components/ServiceConnectionInstructions";
import ServiceConnectionStep from "components/ServiceConnectionStep";

// Disable this check so we match the backend regex exactly.
// eslint-disable-next-line no-useless-escape
const WEBEX_WEBHOOK_REGEX =
  /^https:\/\/[a-zA-Z0-9-.]+\/v1\/webhooks\/incoming\/[a-zA-Z0-9-]+$/;

const WebexWebhookConnectSetupPage = memo(
  function WebexWebhookConnectSetupPage() {
    const [, connectionActions] = useConnectionStore();

    const [webhookUrl, setWebhookUrl] = useState("");
    const [inputError, setInputError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [complete, setComplete] = useState(false);
    const [uuid, setUuid] = useState("");

    const connect = () => {
      setLoading(true);
      connectionActions.webexWebhookConnect({
        webhookUrl,
        onSuccess,
        onError,
      });
    };

    const reset = () => {
      setWebhookUrl("");
      setInputError(false);
    };

    const onSuccess = (data: any) => {
      setLoading(false);
      toast.success("Successfully connected Webex Webhooks", {
        position: "bottom-center",
      });
      connectionActions.loadConnections({ reload: true });
      setComplete(true);
      setUuid(data.uuid);
    };

    const onError = () => {
      setLoading(false);
      toast.error("Something went wrong connecting Webex Webhooks", {
        position: "bottom-center",
      });
    };

    return (
      <>
        <Breadcrumbs>
          <Breadcrumb
            underline="none"
            color="inherit"
            component={Link}
            to="/environment/destinations"
          >
            Environment
          </Breadcrumb>
          <Breadcrumb
            underline="none"
            color="inherit"
            component={Link}
            to="/environment/destinations"
          >
            Destinations
          </Breadcrumb>
          <span />
        </Breadcrumbs>

        <ServiceConnectionInstructions
          label="Cisco Webex Teams Webhook Connection"
          forceCollapse={complete ? new Date() : undefined}
        >
          <Stack spacing={2}>
            <Typography variant="body1" fontWeight="bold">
              How ThreatKey Cisco Webex Teams Webhooks work:
            </Typography>
            <Typography variant="body1">
              ThreatKey Cisco Webex Teams Webhooks are a way for the ThreatKey
              platform to communicate with a Cisco Webex Teams instance in near
              real-time. When an event happens in ThreatKey, a webhook is sent
              to the Cisco Webex Teams instance with information about the
              event. The Cisco Webex Teams instance can then take action based
              on the event.
            </Typography>
            <Typography variant="body1">
              You can use ThreatKey Cisco Webex Teams webhooks to connect to
              Cisco Webex Teams instances, to receive near real-time alerts of
              security events.
            </Typography>
          </Stack>
        </ServiceConnectionInstructions>

        {!complete ? (
          <ServiceConnectionStep disabled={loading}>
            <Typography variant="body1">
              Please paste the <strong>Cisco Webex Teams Webhook URL</strong>{" "}
              that you would like to connect to.
            </Typography>
            <TextField
              fullWidth
              label="Cisco Webex Teams Webhook URL"
              placeholder="https://webexapis.com/v1/webhooks/incoming/<incoming_webhook_url>
              "
              variant="outlined"
              sx={{ mt: 1 }}
              value={webhookUrl}
              onChange={(e: FOTS) => {
                const webhook = e?.target?.value.trim();
                const webhookValid = !(
                  webhook.match(WEBEX_WEBHOOK_REGEX) == null
                );

                setInputError(!!webhook && !webhookValid);
                setWebhookUrl(webhook);
              }}
              error={inputError}
              helperText={
                inputError ? "Invalid Cisco Webex Teams Webhook URL" : ""
              }
              data-private="lipsum"
            />
            <Stack sx={{ mt: 2 }} spacing={2} direction="row">
              <LoadingButton
                variant="contained"
                onClick={connect}
                loading={loading}
                disabled={!webhookUrl || inputError}
              >
                Connect Cisco Webex Teams Webhook
              </LoadingButton>
              <Button variant="text" onClick={reset}>
                Clear
              </Button>
            </Stack>
          </ServiceConnectionStep>
        ) : (
          <ServiceConnectionStep disabled={false}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CheckCircleOutlineRoundedIcon color="success" sx={{ mr: 1 }} />
              <Typography variant="body1">Done!</Typography>
            </Box>
            <Stack sx={{ mt: 2 }} spacing={1} direction="column">
              <Typography variant="body1">
                <Link
                  to={`/environment/destinations/webex_webhook/${uuid}/notifications`}
                >
                  Click here
                </Link>{" "}
                to go to your Cisco Webex Teams Webhook notification settings.
              </Typography>
            </Stack>
          </ServiceConnectionStep>
        )}
      </>
    );
  }
);

export default WebexWebhookConnectSetupPage;
